import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { setReturnTo } from "@/lib/api";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import SignIn from "@/pages/SignIn";
import AuthVerify from "@/pages/AuthVerify";
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminNewOrg = lazy(() => import("@/pages/AdminNewOrg"));
const AdminOrgDetail = lazy(() => import("@/pages/AdminOrgDetail"));
const AdminProjectDetail = lazy(() => import("@/pages/AdminProjectDetail"));
const AdminCommercial = lazy(() => import("@/pages/AdminCommercial"));
const AdminLoaderAllowlist = lazy(() => import("@/pages/AdminLoaderAllowlist"));
const ClientDashboard = lazy(() => import("@/pages/ClientDashboard"));
const ClientProjectWizard = lazy(() => import("@/pages/ClientProjectWizard"));
const ClientOnboarding = lazy(() => import("@/pages/ClientOnboarding"));
const AdminOnboardingReview = lazy(() => import("@/pages/AdminOnboardingReview"));

function Protected({ role, children }) {
  const { user, loading, sessionInvalidReason } = useAuth();
  const loc = useLocation();

  // Wait for /auth/me to resolve before rendering anything — prevents
  // protected-page flash and cross-role navigation.
  if (loading) {
    return (
      <div
        data-testid="protected-loading"
        className="min-h-screen flex items-center justify-center text-sm text-slate-400"
      >
        Loading…
      </div>
    );
  }

  if (!user) {
    setReturnTo(loc.pathname + loc.search);
    const reason = sessionInvalidReason ? `?reason=${sessionInvalidReason}` : "";
    return <Navigate to={`/signin${reason}`} replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/client"} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/auth/verify" element={<AuthVerify />} />

      <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      <Route path="/admin/organizations/new" element={<Protected role="admin"><AdminNewOrg /></Protected>} />
      <Route path="/admin/organizations/:id" element={<Protected role="admin"><AdminOrgDetail /></Protected>} />
      <Route path="/admin/commercial" element={<Protected role="admin"><AdminCommercial /></Protected>} />
      <Route path="/admin/loader-allowlist" element={<Protected role="admin"><AdminLoaderAllowlist /></Protected>} />
      <Route path="/admin/projects/:id" element={<Protected role="admin"><AdminProjectDetail /></Protected>} />
      <Route path="/admin/projects/:id/onboarding" element={<Protected role="admin"><AdminOnboardingReview /></Protected>} />

      <Route path="/client" element={<Protected role="client"><ClientDashboard /></Protected>} />
      <Route path="/client/projects/:id" element={<Protected role="client"><ClientProjectWizard /></Protected>} />
      <Route path="/client/projects/:id/onboarding" element={<Protected role="client"><ClientOnboarding /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-slate-400">Loading workspace…</div>}>
          <AppRoutes />
        </Suspense>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
